import React from 'react';
import PropTypes from 'prop-types';

/**
 * SafeRender Component - Comprehensive protection against React Error #31
 *
 * This component wraps any content and ensures it's safe to render in JSX.
 * It specifically handles objects with {from, to, total} properties that
 * cause React Error #31: "Objects are not valid as a React child"
 */

// Enhanced safe rendering function with comprehensive object handling
const safeRenderContent = (content, context = 'SafeRender') => {
  // Handle null/undefined
  if (content === null || content === undefined) {
    return '';
  }

  // Handle primitives (safe to render)
  if (typeof content === 'string' || typeof content === 'number') {
    return content;
  }

  if (typeof content === 'boolean') {
    return content ? 'Yes' : 'No';
  }

  // Handle objects (potential React Error #31 source)
  if (typeof content === 'object') {
    // Handle null objects
    if (content === null) {
      return '';
    }

    // Handle React elements (should not be processed)
    if (React.isValidElement(content)) {
      return content;
    }

    // Handle Date objects
    if (content instanceof Date) {
      try {
        return content.toLocaleDateString();
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error(`[SafeRender] Failed to format Date object:`, error);
        }
        return 'Invalid Date';
      }
    }

    // Handle arrays
    if (Array.isArray(content)) {
      // Only process arrays that contain primitives, not React components
      const hasComplexObjects = content.some(
        (item) =>
          typeof item === 'object' &&
          item !== null &&
          !React.isValidElement(item) &&
          !(item instanceof Date)
      );

      if (hasComplexObjects) {
        return content
          .map((item, index) => safeRenderContent(item, `${context}[${index}]`))
          .join(', ');
      } else {
        // Simple array of primitives or React elements, return as-is
        return content;
      }
    }

    // CRITICAL FIX: Handle pagination objects with {from, to, total} properties
    if (
      content.total !== undefined ||
      content.from !== undefined ||
      content.to !== undefined
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.error(
          `[SafeRender] Pagination object detected in context ${context}:`,
          content
        );
      }

      // Return the most relevant value
      if (content.total !== undefined) {
        return String(content.total);
      } else if (content.from !== undefined) {
        return String(content.from);
      } else if (content.to !== undefined) {
        return String(content.to);
      }
    }

    // For other objects, return as-is unless they're plain objects
    if (content.constructor === Object) {
      // This is a plain object that could cause React Error #31
      try {
        return JSON.stringify(content);
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error(
            `[SafeRender] Failed to stringify plain object:`,
            error
          );
        }
        return '';
      }
    }

    // For all other objects (components, etc.), return as-is
    return content;
  }

  // Fallback - convert to string
  return String(content);
};

// Runtime validation function that throws errors in development
const validateContent = (content, context) => {
  if (process.env.NODE_ENV === 'development') {
    const type = typeof content;
    const isObject = type === 'object' && content !== null;
    const isArray = Array.isArray(content);
    const isReactElement = React.isValidElement(content);

    if (isObject && !isArray && !isReactElement && !(content instanceof Date)) {
      const keys = Object.keys(content);

      // Check for known problematic object patterns
      if (
        content.total !== undefined ||
        content.from !== undefined ||
        content.to !== undefined
      ) {
        console.error(
          `[SafeRender] VALIDATION ERROR: Pagination object detected at ${context}:`,
          {
            content,
            keys,
            type,
            constructor: content.constructor?.name,
          }
        );
      } else {
        console.warn(
          `[SafeRender] VALIDATION WARNING: Generic object detected at ${context}:`,
          {
            content,
            keys,
            type,
            constructor: content.constructor?.name,
          }
        );
      }
    }
  }
};

/**
 * SafeRender Component
 *
 * @param {*} children - Content to render safely
 * @param {string} context - Context name for debugging
 * @param {boolean} strict - Whether to throw errors for unsafe content in development
 * @param {*} fallback - Fallback content if rendering fails
 */
const SafeRender = ({
  children,
  context = 'unknown',
  strict = false,
  fallback = null,
}) => {
  try {
    // Validate content in development mode
    validateContent(children, context);

    // Process the content safely
    const safeContent = safeRenderContent(children, context);

    // Return processed content
    return safeContent;
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(
        `[SafeRender] Error processing content in context ${context}:`,
        error
      );

      if (strict) {
        throw error;
      }
    }

    // Return fallback or empty string
    return fallback || '';
  }
};

SafeRender.propTypes = {
  children: PropTypes.any,
  context: PropTypes.string,
  strict: PropTypes.bool,
  fallback: PropTypes.any,
};

export default SafeRender;

/**
 * SafeWrapper - Wraps individual components with safe rendering
 *
 * This is a lighter-weight wrapper for individual components that might
 * have issues with object rendering.
 */
const SafeWrapper = ({
  children,
  componentName = 'unknown',
  fallback = null,
}) => {
  const safeChildren = React.Children.map(children, (child, index) => {
    if (
      typeof child === 'object' &&
      child !== null &&
      !React.isValidElement(child)
    ) {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`[SafeWrapper] Object detected in ${componentName}:`, {
          child,
          type: typeof child,
          keys: Object.keys(child),
          childIndex: index,
        });
      }

      return safeRenderContent(child, `${componentName}-${index}`);
    }
    return child;
  });

  return <div className="safe-wrapper">{safeChildren || fallback}</div>;
};

SafeWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  componentName: PropTypes.string,
  fallback: PropTypes.node,
};

/**
 * Higher-Order Component for safe rendering
 *
 * This HOC wraps any component with safe rendering capabilities.
 */
const withSafeRender = (WrappedComponent) => {
  const componentName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const SafeRenderHOC = (props) => {
    // Validate props for objects that shouldn't be rendered directly
    if (process.env.NODE_ENV === 'development') {
      Object.entries(props).forEach(([key, value]) => {
        if (
          typeof value === 'object' &&
          value !== null &&
          !React.isValidElement(value) &&
          !(value instanceof Date) &&
          !Array.isArray(value)
        ) {
          console.warn(
            `[withSafeRender] WARNING: Object detected in ${componentName} props:`,
            {
              propName: key,
              value,
              type: typeof value,
              keys: Object.keys(value),
            }
          );
        }
      });
    }

    return <WrappedComponent {...props} />;
  };

  SafeRenderHOC.displayName = `withSafeRender(${componentName})`;
  SafeRenderHOC.propTypes = WrappedComponent.propTypes;

  return SafeRenderHOC;
};

// Update exports to include the missing components
export { safeRenderContent, SafeWrapper, withSafeRender };
